import { useCallback, useEffect, useRef, useState } from 'react';

export const HIDE_DELTA = 10;
export const HIDE_AFTER_Y = 80;
export const SHOW_BEFORE_Y = 40;
export const SHORT_PAGE_SLACK = 160;
export const SCROLLED_AFTER_Y = 20;

export type HideOnScrollState = {
  hidden: boolean;
  lastY: number;
};

export type HideOnScrollInput = {
  scrollY: number;
  viewportH: number;
  docH: number;
  frozen: boolean;
};

export type UseHideOnScrollOptions = {
  frozen: boolean;
  resetKey: string;
};

export type UseHideOnScrollResult = {
  hidden: boolean;
  scrolled: boolean;
  reveal: () => void;
};

export function nextHideOnScroll(
  prev: HideOnScrollState,
  input: HideOnScrollInput
): HideOnScrollState {
  const y = Math.max(0, input.scrollY);
  const shortPage = input.docH - input.viewportH < SHORT_PAGE_SLACK;

  if (input.frozen || y < SHOW_BEFORE_Y || shortPage) {
    return { hidden: false, lastY: y };
  }

  if (y - prev.lastY >= HIDE_DELTA && y > HIDE_AFTER_Y) {
    return { hidden: true, lastY: y };
  }

  if (prev.lastY - y >= HIDE_DELTA) {
    return { hidden: false, lastY: y };
  }

  return { hidden: prev.hidden, lastY: prev.lastY };
}

function readScrollMetrics() {
  return {
    scrollY: window.scrollY,
    viewportH: window.innerHeight,
    docH: document.documentElement.scrollHeight,
  };
}

export function useHideOnScroll({
  frozen,
  resetKey,
}: UseHideOnScrollOptions): UseHideOnScrollResult {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(
    () => typeof window !== 'undefined' && window.scrollY > SCROLLED_AFTER_Y
  );

  const hiddenRef = useRef(false);
  const lastYRef = useRef(typeof window !== 'undefined' ? Math.max(0, window.scrollY) : 0);
  const frozenRef = useRef(frozen);
  const rafRef = useRef<number | null>(null);

  frozenRef.current = frozen;

  const reveal = useCallback(() => {
    hiddenRef.current = false;
    setHidden(false);
  }, []);

  const apply = useCallback(() => {
    const metrics = readScrollMetrics();
    const y = Math.max(0, metrics.scrollY);
    const next = nextHideOnScroll(
      { hidden: hiddenRef.current, lastY: lastYRef.current },
      { ...metrics, frozen: frozenRef.current }
    );

    lastYRef.current = next.lastY;
    if (next.hidden !== hiddenRef.current) {
      hiddenRef.current = next.hidden;
      setHidden(next.hidden);
    }

    setScrolled((prev) => {
      const nextScrolled = y > SCROLLED_AFTER_Y;
      return prev === nextScrolled ? prev : nextScrolled;
    });
  }, []);

  useEffect(() => {
    hiddenRef.current = false;
    setHidden(false);
    lastYRef.current = Math.max(0, window.scrollY);
    setScrolled(window.scrollY > SCROLLED_AFTER_Y);
  }, [resetKey]);

  useEffect(() => {
    if (!frozen) return;
    hiddenRef.current = false;
    setHidden(false);
  }, [frozen]);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        apply();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    apply();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [apply]);

  return { hidden, scrolled, reveal };
}
